import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useCarsAvailabilityFilters } from "@tenant/cars";
import { isEqual } from "lodash-es";
import { CarsAvailabilityActions } from "~core-cars/store";
import { CarsRawEventName, useEmitCoreCarsRawEvent } from "~core-cars/tracking";

export const useFilterDialog = () => {
  const { context, handlers } = useCarsAvailabilityFilters();
  const filterOptions = context.filterOptions;
  const dispatch = useDispatch();

  const {
    priceRange: priceRangeState,
    supplier: supplierState,
    carType: carTypeState,
    specification: specificationState,
    freeCancellation: freeCancellationState,
    passenger: passengerState,
    minimumBags: minimumBagsState,
  } = context.filters;

  const trackAppliedFilter = useEmitCoreCarsRawEvent(
    CarsRawEventName.CarsAppliedFilter
  );

  const [priceRange, setPriceRange] = useState(priceRangeState);
  const [supplier, setSupplier] = useState(supplierState);
  const [carType, setCarType] = useState(carTypeState);
  const [specification, setSpecification] = useState(specificationState);
  const [freeCancellation, setFreeCancellation] = useState(
    freeCancellationState
  );
  const [passenger, setPassenger] = useState(passengerState);
  const [minimumBags, setMinimumBags] = useState(minimumBagsState);

  const filtersChanged = useMemo(() => {
    return (
      !isEqual(priceRangeState, priceRange) ||
      !isEqual(supplierState, supplier) ||
      !isEqual(carTypeState, carType) ||
      !isEqual(specificationState, specification) ||
      freeCancellationState !== freeCancellation ||
      !isEqual(passengerState, passenger) ||
      !isEqual(minimumBagsState, minimumBags)
    );
  }, [
    carType,
    carTypeState,
    freeCancellation,
    freeCancellationState,
    minimumBags,
    minimumBagsState,
    passenger,
    passengerState,
    priceRange,
    priceRangeState,
    specification,
    specificationState,
    supplier,
    supplierState,
  ]);

  const updateValuesToMatchState = useCallback(() => {
    setPriceRange(priceRangeState);
    setSupplier(supplierState);
    setCarType(carTypeState);
    setSpecification(specificationState);
    setFreeCancellation(freeCancellationState);
    setPassenger(passengerState);
    setMinimumBags(minimumBagsState);
  }, [
    carTypeState,
    freeCancellationState,
    minimumBagsState,
    passengerState,
    priceRangeState,
    specificationState,
    supplierState,
  ]);

  const resetAllFilters = useCallback(() => {
    handlers.resetFilters();
  }, [handlers]);

  const applyFilter = useCallback(() => {
    dispatch(
      CarsAvailabilityActions.setAllFilters({
        priceRange,
        supplier,
        carType,
        specification,
        passenger,
        freeCancellation,
        minimumBags,
      })
    );

    trackAppliedFilter();
  }, [
    carType,
    dispatch,
    freeCancellation,
    minimumBags,
    passenger,
    priceRange,
    specification,
    supplier,
    trackAppliedFilter,
  ]);

  return useMemo(
    () => ({
      context: {
        priceRange,
        supplier,
        carType,
        specification,
        freeCancellation,
        passenger,
        minimumBags,
        filterOptions,
        filtersChanged,
      },
      handlers: {
        updateValuesToMatchState,
        resetAllFilters,
        applyFilter,
        onPriceChange: setPriceRange,
        onSuppliersChange: setSupplier,
        onCarTypeChange: setCarType,
        onSpecificationChange: setSpecification,
        onFreeCancellationChange: setFreeCancellation,
        onPassengerChange: setPassenger,
        onMinimumBagsChange: setMinimumBags,
      },
    }),
    [
      applyFilter,
      carType,
      filterOptions,
      freeCancellation,
      minimumBags,
      passenger,
      priceRange,
      resetAllFilters,
      specification,
      supplier,
      updateValuesToMatchState,
      filtersChanged,
    ]
  );
};
